import { useQuery } from 'react-query';
import { useEffect } from 'react';
import { getCustomerSubscription } from 'modules/api';
import env from 'environments';
import { subscriptionPollingUtils } from '../subscriptionPolling';
import type { PlanChange } from '../subscriptionNotice';

type SubscriptionCancellationReturn = {
  isScheduledForCancellation: boolean;
  scheduledCancelDate: string | null;
  scheduledPlanChange: PlanChange | null;
  lastPlanChange: PlanChange | null;
  isLoading: boolean;
};

export const useSubscriptionCancellation = (): SubscriptionCancellationReturn => {
  const shouldPoll = subscriptionPollingUtils.shouldPoll();

  const { data: subscription, isLoading } = useQuery(
    'getCustomerSubscription',
    getCustomerSubscription,
    {
      enabled: env.ENABLE_CLICK_TO_CANCEL,
      retry: false,
      staleTime: shouldPoll ? 0 : 5 * 60 * 1000, // No stale time during polling
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchInterval: shouldPoll ? 5000 : false, // Poll every 5 seconds if active
      refetchIntervalInBackground: false,
    }
  );

  // Increment poll count on each refetch
  useEffect(() => {
    if (shouldPoll) {
      subscriptionPollingUtils.incrementPollCount();
    }
  }, [subscription, shouldPoll]);

  // Handle nested subscription data from Chargebee API
  const actualSubscription = subscription?.list?.[0]?.subscription || subscription;

  // Check subscription status
  const chargebeeStatus = actualSubscription?.chargebeeSubscriptionStatus;
  const dbStatus = actualSubscription?.status || actualSubscription?.state;
  const subscriptionStatus = chargebeeStatus || dbStatus;

  const isSubscriptionCanceled =
    subscriptionStatus === 'canceled' || subscriptionStatus === 'cancelled';

  // Check for scheduled cancellation date
  const scheduledCancelDate =
    actualSubscription?.cancellationScheduledAt ||
    actualSubscription?.cancelled_at ||
    actualSubscription?.cancelledAt ||
    actualSubscription?.cancel_date ||
    actualSubscription?.cancelAt ||
    null;

  // Backend returns 'cancellation_scheduled' in status and 'non_renewing' in chargebeeSubscriptionStatus
  const isScheduledForCancellation =
    !isSubscriptionCanceled &&
    (dbStatus === 'cancellation_scheduled' ||
      chargebeeStatus === 'non_renewing' ||
      !!scheduledCancelDate ||
      !!actualSubscription?.cancelScheduleCreatedAt);

  return {
    isScheduledForCancellation,
    scheduledCancelDate,
    scheduledPlanChange: actualSubscription?.scheduledPlanChange || null,
    lastPlanChange: actualSubscription?.lastPlanChange || null,
    isLoading,
  };
};
